import { AlertTriangle, CheckCircle2, Crosshair, QrCode } from 'lucide-react';
import { t, useLanguage, type Language } from '@/app/utils/i18n';
import type { AqlGrowthCalibration } from '@/app/services/growth-service';

interface CalibrationStatusCardProps {
  calibration: AqlGrowthCalibration | null;
  loading?: boolean;
}

const DATE_LOCALES: Record<Language, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};

const formatDate = (value: string | null, language: Language) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(DATE_LOCALES[language], { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatRatio = (ratio: number | null) =>
  ratio === null || ratio === undefined ? '—' : `${ratio.toFixed(4)} mm/px`;

const formatQuality = (score: number | null) =>
  score === null || score === undefined ? '—' : `${Math.round(score * 100)}%`;

export function CalibrationStatusCard({ calibration, loading }: CalibrationStatusCardProps) {
  const language = useLanguage();
  const tr = (key: string, params?: Record<string, unknown>) => t('vision', key, language, params);
  const calibrationType = calibration?.calibration_type === 'video_ultrasound'
    ? tr('videoUltrasoundCalibration')
    : calibration?.calibration_type === 'video'
      ? tr('videoCalibration')
      : calibration?.calibration_type === 'ultrasound'
        ? tr('ultrasoundCalibration')
        : calibration?.calibration_type ?? tr('manual');

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="h-4 w-32 animate-pulse rounded bg-muted/70" />
        <div className="mt-3 h-16 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  if (!calibration) {
    return (
      <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-amber-300" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-100">
              {tr('calibrationMissing')}
            </h3>
            <p className="mt-1 text-sm text-amber-800/85 dark:text-amber-200/80">
              {tr('calibrationMissingDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
          <h3 className="text-sm font-semibold text-foreground">{tr('activeCalibration')}</h3>
        </div>
        <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {calibrationType}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('pixelRatio')}</dt>
          <dd className="mt-1 font-mono text-sm font-semibold text-foreground">
            {formatRatio(calibration.pixel_to_mm_ratio)}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('quality')}</dt>
          <dd className="mt-1 font-mono text-sm font-semibold text-foreground">
            {formatQuality(calibration.quality_score)}
          </dd>
        </div>
        <div className="flex items-start gap-2">
          <Crosshair className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('position')}</dt>
            <dd className="mt-1 font-mono text-sm text-foreground">{calibration.position_code ?? '—'}</dd>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <QrCode className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('qrTag')}</dt>
            <dd className="mt-1 truncate font-mono text-sm text-foreground" title={calibration.qr_code ?? ''}>
              {calibration.qr_code ?? '—'}
            </dd>
          </div>
        </div>
      </dl>

      <p className="mt-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
        {tr('validFrom', { date: formatDate(calibration.valid_from, language) })}
        {calibration.valid_to ? ` · ${tr('validUntil', { date: formatDate(calibration.valid_to, language) })}` : ''}
      </p>
      <div className="mt-3 rounded-xl border border-border bg-muted/30 px-3 py-2 text-xs">
        <div className="flex items-center justify-between gap-3"><span className="font-semibold text-foreground">Modelo de profundidade</span><span className={calibration.depth_model_name ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}>{calibration.depth_model_name ? 'Ativo' : 'Não configurado'}</span></div>
        <p className="mt-1 truncate font-mono text-[10px] text-muted-foreground" title={calibration.depth_model_name ?? ''}>{calibration.depth_model_name ?? '—'}{calibration.depth_model_version ? ` · ${calibration.depth_model_version}` : ''}</p>
      </div>
    </div>
  );
}
